<template>
  <b-modal
    id="modal-nuovo-canale-chat"
    ref="modal-nuovo-canale-chat"
    :title="$t('ModalNuovoCanale.titolo')"
    size="lg"
    :ok-title="$t('ModalNuovoCanale.conferma')"
    :cancel-title="$t('annulla')"
    :ok-disabled="!selectedOperator || !selectedPatient || saving"
    ok-variant="popup-button"
    cancel-variant="popup-button mr-2"
    footer-border-variant="white"
    @ok="handleOk"
    @cancel="$bvModal.hide('modal-nuovo-canale-chat')"
    @show="handleShow"
    centered
  >
    <div class="modal-scroll px-4 py-3">
      <b-form @submit.stop.prevent="handleSubmit">
        <!-- Operatore: chi gestisce le chat della struttura lo sceglie, gli
             altri aprono la chat a proprio nome (lo stesso controllo del server) -->
        <b-form-group
          v-if="!puoGestire"
          :label="$t('ModalNuovoCanale.label_operatore')"
          label-for="select-operator"
          :description="$t('ModalNuovoCanale.descrizione_operatore_tu')"
        >
          <b-form-input id="select-operator" :value="nomeUtente" readonly></b-form-input>
        </b-form-group>
        <b-form-group
          v-else
          :label="$t('ModalNuovoCanale.label_operatore')"
          label-for="select-operator"
          :description="$t('ModalNuovoCanale.descrizione_operatore')"
        >
          <b-form-select
            id="select-operator"
            v-model="selectedOperator"
            :options="operatorOptions"
            :disabled="loadingOperators"
            :aria-label="$t('ModalNuovoCanale.label_operatore')"
            required
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                {{ $t('ModalNuovoCanale.scegli_operatore') }}
              </b-form-select-option>
            </template>
          </b-form-select>
          <b-spinner
            small
            v-if="loadingOperators"
            class="mt-1"
            :label="$t('ModalNuovoCanale.caricamento')"
          ></b-spinner>
        </b-form-group>

        <!-- Paziente: dalla scheda del paziente è già scelto; la ricerca serve
             a chi gestisce le chat, l'unico che può elencare i pazienti -->
        <b-form-group
          v-if="presetPatientHashid"
          :label="$t('ModalNuovoCanale.label_paziente')"
          label-for="input-patient-search"
        >
          <b-form-input id="input-patient-search" :value="presetPatientName" readonly></b-form-input>
        </b-form-group>
        <b-form-group
          v-else-if="puoGestire"
          :label="$t('ModalNuovoCanale.label_paziente')"
          label-for="input-patient-search"
          :description="$t('ModalNuovoCanale.descrizione_paziente')"
        >
          <b-input-group>
            <b-form-input
              id="input-patient-search"
              v-model="patientSearch"
              :placeholder="$t('ModalNuovoCanale.placeholder_ricerca')"
              :aria-label="$t('ModalNuovoCanale.placeholder_ricerca')"
              @input="debounceSearchPatients"
            ></b-form-input>
            <b-input-group-append>
              <b-button
                variant="outline-secondary"
                :aria-label="$t('ModalNuovoCanale.cerca')"
                @click="fetchPatients"
              >
                <b-icon icon="search" aria-hidden="true"></b-icon>
              </b-button>
            </b-input-group-append>
          </b-input-group>

          <b-form-select
            class="mt-2"
            v-model="selectedPatient"
            :options="patientOptions"
            :disabled="loadingPatients"
            :aria-label="$t('ModalNuovoCanale.label_paziente')"
            required
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                {{ $t('ModalNuovoCanale.scegli_paziente') }}
              </b-form-select-option>
            </template>
          </b-form-select>
          <b-spinner
            small
            v-if="loadingPatients"
            class="mt-1"
            :label="$t('ModalNuovoCanale.caricamento')"
          ></b-spinner>
        </b-form-group>
        <!-- Senza il permesso non si elencano i pazienti: la chat si apre
             dalla scheda del paziente, dove il paziente è già scelto -->
        <p v-else class="list-row__meta mb-0">{{ $t('ModalNuovoCanale.paziente_dalla_scheda') }}</p>

        <b-alert variant="info" show class="small mt-3" role="status">
          <b-icon icon="info-circle" class="mr-1" aria-hidden="true"></b-icon>
          {{ $t('ModalNuovoCanale.nota') }}
        </b-alert>
      </b-form>
    </div>
  </b-modal>
</template>

<script>
import { chatService } from "@/_services";

export default {
  name: "ModalNuovoCanale",
  props: {
    presetPatientHashid: {
      type: String,
      default: null,
    },
    presetPatientName: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      selectedOperator: null,
      selectedPatient: null,
      patientSearch: "",
      operators: [],
      patients: [],
      loadingOperators: false,
      loadingPatients: false,
      saving: false,
      searchTimeout: null,
    };
  },
  computed: {
    operatorOptions() {
      return this.operators.map((op) => {
        const cognome = op.surname || op.cognome || "";
        const nome = op.name || op.nome || "";
        const fullname = (cognome || nome) ? `${cognome} ${nome}`.trim() : "";
        const label = fullname ? `${fullname} (${op.email})` : op.email;
        return {
          value: op.hashid,
          text: label,
        };
      });
    },
    patientOptions() {
      if (this.presetPatientHashid && this.presetPatientName) {
        return [
          {
            value: this.presetPatientHashid,
            text: this.presetPatientName,
          },
        ];
      }
      return this.patients.map((p) => {
        const anag = (p && typeof p.anagrafica === "object" && p.anagrafica !== null) ? p.anagrafica : {};
        const cognome = anag.surname || anag.cognome || p.surname || p.cognome || "";
        const nome = anag.name || anag.nome || p.name || p.nome || "";
        const cf = anag.cf || anag.fiscal_code || anag.codice_fiscale ? ` - CF: ${anag.cf || anag.fiscal_code || anag.codice_fiscale}` : "";
        const fullname = (cognome || nome) ? `${cognome} ${nome}`.trim() : "";
        const label = fullname ? `${fullname}${cf} (${p.email})` : (p.email || p.hashid);
        return {
          value: p.hashid,
          text: label,
        };
      });
    },
    // L'utente sta nel modulo «utente» dello store: qui si leggeva «account»,
    // che non esiste, quindi l'operatore non veniva mai proposto
    utente() {
      return (this.$store && this.$store.state.utente && this.$store.state.utente.user) || {};
    },
    // Chi gestisce le chat della struttura apre la chat a qualunque medico
    // abilitato e cerca fra i pazienti; gli altri la aprono solo a proprio
    // nome. Senza il permesso gli elenchi dei medici e dei pazienti rispondono
    // 403: era il «Non è stato possibile recuperare gli operatori idonei»
    // che vedeva un medico qualunque.
    puoGestire() {
      return Array.isArray(this.utente.permessi) && this.utente.permessi.includes("gestire chat pazienti");
    },
    nomeUtente() {
      const anag = this.utente.anagrafica || {};
      const nome = `${anag.name || ""} ${anag.surname || ""}`.trim();
      return nome || this.utente.email || "";
    },
  },
  methods: {
    handleShow() {
      this.selectedPatient = this.presetPatientHashid || null;
      this.patientSearch = "";
      if (!this.puoGestire) {
        this.selectedOperator = this.utente.hashid || null;
        return;
      }
      this.selectedOperator = null;
      this.fetchOperators();
      if (!this.presetPatientHashid) {
        this.fetchPatients();
      }
    },
    fetchOperators() {
      this.loadingOperators = true;
      chatService
        .getEligibleOperators()
        .then((res) => {
          this.operators = res.data?.data || [];
          const currentUser = this.utente;
          if (currentUser.hashid && this.operators.some((o) => o.hashid === currentUser.hashid)) {
            this.selectedOperator = currentUser.hashid;
          } else if (this.operators.length === 1) {
            this.selectedOperator = this.operators[0].hashid;
          }
        })
        .catch(() => {
          this.$bvToast.toast(this.$t("ModalNuovoCanale.errore_operatori"), {
            variant: "danger",
            solid: true,
          });
        })
        .finally(() => {
          this.loadingOperators = false;
        });
    },
    debounceSearchPatients() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchPatients();
      }, 350);
    },
    fetchPatients() {
      this.loadingPatients = true;
      chatService
        .getEligiblePatients({ search: this.patientSearch })
        .then((res) => {
          this.patients = res.data?.data?.data || res.data?.data || [];
        })
        .catch(() => {
          this.patients = [];
        })
        .finally(() => {
          this.loadingPatients = false;
        });
    },
    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },
    handleSubmit() {
      if (!this.selectedOperator || !this.selectedPatient) {
        return;
      }
      this.saving = true;
      chatService
        .enableChannel({
          operator_user_id: this.selectedOperator,
          patient_user_id: this.selectedPatient,
        })
        .then((res) => {
          this.$bvToast.toast(this.$t("ModalNuovoCanale.canale_creato"), {
            title: this.$t("ModalNuovoCanale.titolo_successo"),
            variant: "success",
            solid: true,
          });
          this.$emit("channel-created", res.data?.data);
          this.$bvModal.hide("modal-nuovo-canale-chat");
        })
        .catch((err) => {
          const msg = err?.data?.message || this.$t("ModalNuovoCanale.errore_creazione");
          this.$bvToast.toast(msg, {
            title: this.$t("ModalNuovoCanale.titolo_errore"),
            variant: "danger",
            solid: true,
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>
